import React, { useCallback, useEffect, useRef, useState } from 'react';
import { nanoid } from 'nanoid';
import { rootStore } from '../../../../mobx/rootStore';
import { createNewComment, updateTaskDetails } from '../../../../services/taskServices';
import { getRawComment } from '../../../../helpers/common';
import { InputTitle } from './styles';
import { COMMENT_TYPE } from '../../../../global/constants';

const TaskTitle = () => {
  //Mobx state variables
  const { taskStore, currentUser } = rootStore;
  const { currentTask: taskDetails } = taskStore;
  // Title editing state
  const [isEditingTitle, setIsEditingTitle] = useState(false);
  const [titleValue, setTitleValue] = useState<string>(taskDetails?.name || '');
  // ref variables
  const titleRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    setTitleValue(taskDetails?.name || '');
  }, [taskDetails?.name]);

  const beginEditTitle = useCallback(() => {
    // Enable editing and focus without forcing caret to end
    setIsEditingTitle(true);
    setTimeout(() => {
      titleRef.current?.focus();
    }, 0);
  }, []);

  // Ensure first interaction places caret where the user clicks
  const handleMouseDown = useCallback(() => {
    if (!isEditingTitle) {
      setIsEditingTitle(true);
      // Do not manipulate selection; let the browser place caret at click position
    }
  }, [isEditingTitle]);

  const saveTitle = useCallback(async () => {
    if (!taskDetails?.id) {
      setIsEditingTitle(false);
      return;
    }
    const newTitle = (titleRef.current?.textContent || titleValue || '').trim();
    const finalTitle = newTitle.length === 0 ? taskDetails.name || '' : newTitle;
    if (finalTitle === taskDetails.name) {
      setIsEditingTitle(false);
      return;
    }
    try {
      // Optimistic update
      rootStore.taskStore.currentTask?.setName(finalTitle);
      const payloadTask = { name: finalTitle, projectId: taskDetails?.projectId };
      const response = await updateTaskDetails(taskDetails.id, payloadTask);
      if (response) {
        // Add the activity row locally too, the API call alone does not put it in the list.
        const commentId = currentUser ? nanoid() : '';
        if (currentUser) {
          taskDetails.upsertComment(getRawComment({ id: commentId, type: COMMENT_TYPE.UPDATE_TASK }, currentUser));
        }
        const commentResponse = await createNewComment(taskDetails.id, {
          Type: COMMENT_TYPE.UPDATE_TASK
        });
        if (!commentResponse && commentId) {
          // revert the local activity row if the server never recorded it
          taskDetails.removeComment(commentId);
        }
      }
    } catch (e) {
      // revert on error
      rootStore.taskStore.currentTask?.setName(taskDetails.name || '');
    } finally {
      setIsEditingTitle(false);
    }
  }, [taskDetails, titleValue, currentUser]);

  const handleTitleKeyDown = useCallback(
    (e: React.KeyboardEvent<HTMLDivElement>) => {
      if (e.key === 'Enter') {
        e.preventDefault();
        saveTitle();
      }
    },
    [saveTitle]
  );
  return (
    <>
      <InputTitle
        role='button'
        ref={titleRef}
        contentEditable={isEditingTitle}
        suppressContentEditableWarning
        onMouseDown={handleMouseDown}
        onClick={() => !isEditingTitle && beginEditTitle()}
        onBlur={() => isEditingTitle && saveTitle()}
        onKeyDown={isEditingTitle ? handleTitleKeyDown : undefined}>
        {titleValue}
      </InputTitle>
    </>
  );
};

export default TaskTitle;
